import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Edit2, Trash2, Tag as TagIcon, Plus, Info } from 'lucide-react'
import { tagsApi, TAG_COLORS, CreateTagDto } from '@/services/tagsApi'

interface TagFormProps {
  initial?: { name: string; color: string }
  loading?: boolean
  onSave: (name: string, color: string) => void
  onCancel: () => void
  saveLabel?: string
}

function TagForm({ initial, loading, onSave, onCancel, saveLabel = 'Guardar' }: TagFormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [color, setColor] = useState(initial?.color ?? TAG_COLORS[0].value)

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Nombre</label>
        <input
          className="w-full rounded-lg border border-slate-200 bg-[#f6f8f8] text-slate-900 focus:border-[#075f55] focus:ring-[#075f55]/20 focus:ring-1 focus:outline-none h-12 px-4 text-sm"
          placeholder="Nombre de la etiqueta"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={50}
          autoFocus
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-3">Elige un color</label>
        <div className="flex flex-wrap gap-3">
          {TAG_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setColor(c.value)}
              title={c.name}
              className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                color === c.value ? 'ring-2 ring-offset-2 ring-[#075f55] scale-110' : ''
              }`}
              style={{ backgroundColor: c.value }}
            />
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => name.trim() && onSave(name.trim(), color)}
          disabled={!name.trim() || loading}
          className="flex-1 bg-[#075f55] text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#064e46] transition-colors text-sm"
        >
          <Plus className="h-4 w-4" />
          {loading ? 'Guardando...' : saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-3 rounded-lg bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

export default function TagsSettings() {
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateTagDto) => tagsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, name, color }: { id: string; name: string; color: string }) =>
      tagsApi.update(id, { name, color }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tagsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      setDeletingId(null)
    },
  })

  return (
    <div className="max-w-lg space-y-6 pb-10">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Gestión de Etiquetas</h1>
      </div>

      {/* Info box */}
      <div className="p-4 bg-[#075f55]/10 border border-[#075f55]/20 rounded-xl">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-[#075f55] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-[#075f55]">Sobre las etiquetas</h4>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Usa etiquetas para categorizar conversaciones y priorizar clientes. Las etiquetas ayudan a tu equipo a filtrar y encontrar chats relevantes rápidamente.
            </p>
          </div>
        </div>
      </div>

      {/* Create form */}
      <div>
        <h3 className="text-base font-bold text-slate-900 mb-4">Nueva etiqueta</h3>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <TagForm
            key={isCreating ? 'creating' : 'idle'}
            loading={createMutation.isPending}
            onSave={(name, color) => createMutation.mutate({ name, color })}
            onCancel={() => setIsCreating(false)}
            saveLabel="Crear etiqueta"
          />
          {createMutation.isError && (
            <p className="mt-2 text-xs text-red-600">Error al crear la etiqueta. Intenta nuevamente.</p>
          )}
        </div>
      </div>

      {/* Existing tags */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Etiquetas existentes</h3>
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded">
            {tags.length} etiquetas
          </span>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 bg-white rounded-lg border border-slate-100 animate-pulse" />
            ))}
          </div>
        ) : tags.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-xl border border-slate-100">
            <TagIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No hay etiquetas. Crea la primera.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tags.map((tag) => (
              <div key={tag.id}>
                {editingId === tag.id ? (
                  <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                    <TagForm
                      initial={{ name: tag.name, color: tag.color }}
                      loading={updateMutation.isPending}
                      onSave={(name, color) => updateMutation.mutate({ id: tag.id, name, color })}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                ) : deletingId === tag.id ? (
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{tag.name}</p>
                      <p className="text-xs text-red-600 mt-0.5">¿Confirmas que deseas eliminar esta etiqueta?</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => deleteMutation.mutate(tag.id)}
                        disabled={deleteMutation.isPending}
                        className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
                      >
                        {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="bg-slate-100 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                      <span className="text-sm font-medium text-slate-900">{tag.name}</span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingId(tag.id)}
                        className="p-2 text-slate-400 hover:text-[#075f55] transition-colors rounded-lg hover:bg-slate-50"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingId(tag.id)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-slate-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
